/**
 * ProgressionSystem - Handles area unlocks, rod upgrades, and progression
 */

import { eventBus, GameEvents } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import {
  STORAGE_KEYS,
  PROGRESSION_THRESHOLDS,
  ROD_BONUSES,
  FISHING_HOLE_POSITIONS
} from '../core/Constants.js';

// Pond unlock definitions
const POND_UNLOCKS = {
  tier1: {
    ponds: [0, 1, 2, 3],
    name: 'Starting Ponds',
    description: 'Available from the start',
    condition: null // Always unlocked
  },
  tier2: {
    ponds: [4, 5],
    name: 'Forest & Sunny Ponds',
    description: 'Unlocked after catching 20 fish',
    condition: { fishCaught: 20 }
  },
  tier3: {
    ponds: [6, 7],
    name: 'Hidden & Deep Ponds',
    description: 'Unlocked after discovering 12 species',
    condition: { speciesDiscovered: 12 }
  }
};

// Rod upgrade definitions
const ROD_UPGRADES = {
  1: {
    name: 'Basic Rod',
    description: 'A simple fishing rod',
    effect: 'Normal catch window',
    condition: null // Starting rod
  },
  2: {
    name: 'Improved Rod',
    description: 'A better quality fishing rod',
    effect: '+10% catch window',
    condition: { fishCaught: 25 }
  },
  3: {
    name: 'Professional Rod',
    description: 'A professional-grade fishing rod',
    effect: '+20% catch window',
    condition: { fishCaught: 50 }
  },
  4: {
    name: 'Master Rod',
    description: 'The finest fishing rod available',
    effect: '+30% catch window, +5% rare fish chance',
    condition: { speciesDiscovered: 10 }
  }
};

// Fish pool for each pond (location-specific fish)
const POND_FISH_POOLS = {
  0: { // Starter Pond
    name: 'Starter Pond',
    fish: [
      { id: 'azure_tidefish', weight: 40 },
      { id: 'jade_minnow', weight: 30 },
      { id: 'rosefin_tetra', weight: 20 },
      { id: 'red_snapper', weight: 10 }
    ]
  },
  1: { // Meadow Pond
    name: 'Meadow Pond',
    fish: [
      { id: 'azure_tidefish', weight: 20 },
      { id: 'sunset_guppy', weight: 25 },
      { id: 'clownfish_comet', weight: 20 },
      { id: 'ember_dottyback', weight: 15 },
      { id: 'sunspot_puffer', weight: 12 },
      { id: 'golden_sunfish', weight: 8 }
    ]
  },
  2: { // Garden Pond
    name: 'Garden Pond',
    fish: [
      { id: 'jade_minnow', weight: 25 },
      { id: 'magenta_mopey', weight: 20 },
      { id: 'indigo_goofball', weight: 20 },
      { id: 'cerulean_wrasse', weight: 15 },
      { id: 'fuchsia_fighter', weight: 12 },
      { id: 'violet_fantail', weight: 8 }
    ]
  },
  3: { // Rocky Pond
    name: 'Rocky Pond',
    fish: [
      { id: 'rosefin_tetra', weight: 20 },
      { id: 'red_snapper', weight: 20 },
      { id: 'ember_dottyback', weight: 18 },
      { id: 'clownfish_comet', weight: 15 },
      { id: 'reef_sharklet', weight: 15 },
      { id: 'golden_sunfish', weight: 12 }
    ]
  },
  4: { // Forest Pond (Tier 2)
    name: 'Forest Pond',
    fish: [
      { id: 'emerald_samurai', weight: 22 },
      { id: 'violet_queenfish', weight: 18 },
      { id: 'jade_minnow', weight: 15 },
      { id: 'cerulean_wrasse', weight: 15 },
      { id: 'reef_sharklet', weight: 15 },
      { id: 'golden_sunfish', weight: 15 }
    ]
  },
  5: { // Sunny Pond (Tier 2)
    name: 'Sunny Pond',
    fish: [
      { id: 'sunset_guppy', weight: 20 },
      { id: 'sunspot_puffer', weight: 18 },
      { id: 'golden_sunfish', weight: 18 },
      { id: 'fuchsia_fighter', weight: 15 },
      { id: 'violet_fantail', weight: 15 },
      { id: 'emerald_samurai', weight: 14 }
    ]
  },
  6: { // Hidden Pond (Tier 3)
    name: 'Hidden Pond',
    fish: [
      { id: 'violet_queenfish', weight: 25 },
      { id: 'emerald_samurai', weight: 22 },
      { id: 'reef_sharklet', weight: 20 },
      { id: 'violet_fantail', weight: 18 },
      { id: 'golden_sunfish', weight: 10 },
      { id: 'abyssal_angler', weight: 5 }
    ]
  },
  7: { // Deep Pond (Tier 3 - Legendary)
    name: 'Deep Pond',
    fish: [
      { id: 'abyssal_angler', weight: 15 },
      { id: 'reef_sharklet', weight: 20 },
      { id: 'emerald_samurai', weight: 20 },
      { id: 'violet_queenfish', weight: 20 },
      { id: 'cerulean_wrasse', weight: 15 },
      { id: 'golden_sunfish', weight: 10 }
    ]
  }
};

class ProgressionSystem {
  constructor() {
    this.loadProgress();
    this.setupEventListeners();
  }

  /**
   * Load progression from localStorage
   */
  loadProgress() {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.PROGRESSION);
      if (saved) {
        const data = JSON.parse(saved);
        // Merge with gameState
        if (data.unlockedPonds) {
          gameState.state.unlockedPonds = data.unlockedPonds;
        }
        if (data.rodLevel) {
          gameState.state.rodLevel = data.rodLevel;
        }
      }
    } catch (error) {
      console.warn('Failed to load progression:', error);
    }
  }

  /**
   * Save progression to localStorage
   */
  saveProgress() {
    try {
      const data = {
        unlockedPonds: gameState.state.unlockedPonds,
        rodLevel: gameState.state.rodLevel
      };
      localStorage.setItem(STORAGE_KEYS.PROGRESSION, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save progression:', error);
    }
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    eventBus.on(GameEvents.FISH_CAUGHT, () => {
      this.checkUnlocks();
    });
  }

  /**
   * Check for new unlocks based on current progress
   */
  checkUnlocks() {
    this.checkPondUnlocks();
    this.checkRodUpgrades();
  }

  /**
   * Check for pond tier unlocks
   */
  checkPondUnlocks() {
    const state = gameState.state;
    const discoveredCount = this.getDiscoveredCount();

    // Check Tier 2
    if (state.fishCaught >= PROGRESSION_THRESHOLDS.ponds_tier2.fishCaught) {
      POND_UNLOCKS.tier2.ponds.forEach(pondIndex => {
        if (!state.unlockedPonds.includes(pondIndex)) {
          this.unlockPond(pondIndex, 'tier2');
        }
      });
    }

    // Check Tier 3
    if (discoveredCount >= PROGRESSION_THRESHOLDS.ponds_tier3.speciesDiscovered) {
      POND_UNLOCKS.tier3.ponds.forEach(pondIndex => {
        if (!state.unlockedPonds.includes(pondIndex)) {
          this.unlockPond(pondIndex, 'tier3');
        }
      });
    }
  }

  /**
   * Check for rod upgrades
   */
  checkRodUpgrades() {
    const state = gameState.state;
    const discoveredCount = this.getDiscoveredCount();

    // Check each rod level
    if (state.rodLevel < 2 && state.fishCaught >= PROGRESSION_THRESHOLDS.rod_level2.fishCaught) {
      this.upgradeRod(2);
    }

    if (state.rodLevel < 3 && state.fishCaught >= PROGRESSION_THRESHOLDS.rod_level3.fishCaught) {
      this.upgradeRod(3);
    }

    if (state.rodLevel < 4 && discoveredCount >= PROGRESSION_THRESHOLDS.rod_level4.speciesDiscovered) {
      this.upgradeRod(4);
    }
  }

  /**
   * Get count of discovered species
   */
  getDiscoveredCount() {
    const inventory = gameState.state.inventory;
    if (!inventory) return 0;
    return Object.values(inventory).filter(entry => entry.discovered).length;
  }

  /**
   * Unlock a pond
   * @param {number} pondIndex - Index of the pond to unlock
   * @param {string} tier - Tier name for notification
   */
  unlockPond(pondIndex, tier) {
    const state = gameState.state;

    if (!state.unlockedPonds.includes(pondIndex)) {
      state.unlockedPonds.push(pondIndex);
      this.saveProgress();

      const pondName = FISHING_HOLE_POSITIONS[pondIndex]?.name || `Pond ${pondIndex + 1}`;

      eventBus.emit(GameEvents.AREA_UNLOCKED, {
        pondIndex,
        pondName,
        tier
      });

      console.log(`Unlocked: ${pondName}`);
    }
  }

  /**
   * Upgrade the fishing rod
   * @param {number} level - New rod level
   */
  upgradeRod(level) {
    const state = gameState.state;

    if (level > state.rodLevel) {
      state.rodLevel = level;
      this.saveProgress();

      const rodInfo = ROD_UPGRADES[level];

      eventBus.emit(GameEvents.ROD_UPGRADED, {
        level,
        name: rodInfo.name,
        effect: rodInfo.effect
      });

      console.log(`Rod upgraded to: ${rodInfo.name}`);
    }
  }

  /**
   * Check if a pond is unlocked
   * @param {number} pondIndex - Pond index
   */
  isPondUnlocked(pondIndex) {
    return gameState.state.unlockedPonds.includes(pondIndex);
  }

  /**
   * Get the current rod bonus
   */
  getRodBonus() {
    const level = gameState.state.rodLevel;
    return ROD_BONUSES[level] || ROD_BONUSES[1];
  }

  /**
   * Get fish pool for a specific pond
   * @param {number} pondIndex - Pond index
   * @param {Array} fishTypes - Available fish types
   */
  getFishPoolForPond(pondIndex, fishTypes) {
    const pool = POND_FISH_POOLS[pondIndex];
    if (!pool) return null;

    return {
      name: pool.name,
      fish: pool.fish.map(entry => ({
        fish: fishTypes.find(f => f.id === entry.id),
        weight: entry.weight
      })).filter(entry => entry.fish) // Filter out any missing fish
    };
  }

  /**
   * Get a random fish from a pond's pool
   * @param {number} pondIndex - Pond index
   * @param {Array} fishTypes - Available fish types
   */
  getRandomFishFromPond(pondIndex, fishTypes) {
    const pool = this.getFishPoolForPond(pondIndex, fishTypes);
    if (!pool || !pool.fish.length) return null;

    // Apply rod bonus to rare fish
    const rodBonus = this.getRodBonus();
    let adjustedFish = pool.fish.map(entry => {
      let weight = entry.weight;

      // Increase weight for rare/legendary fish if rod has bonus
      if (rodBonus.rareBonus > 0) {
        if (entry.fish.rarity === 'rare' || entry.fish.rarity === 'legendary') {
          weight *= (1 + rodBonus.rareBonus);
        }
      }

      return { fish: entry.fish, weight };
    });

    // Calculate total weight
    const totalWeight = adjustedFish.reduce((sum, e) => sum + e.weight, 0);

    // Random selection
    let random = Math.random() * totalWeight;
    for (const entry of adjustedFish) {
      random -= entry.weight;
      if (random <= 0) {
        return entry.fish;
      }
    }

    // Fallback
    return adjustedFish[0].fish;
  }

  /**
   * Get pond info for UI
   * @param {number} pondIndex - Pond index
   */
  getPondInfo(pondIndex) {
    const position = FISHING_HOLE_POSITIONS[pondIndex];
    const pool = POND_FISH_POOLS[pondIndex];
    const unlocked = this.isPondUnlocked(pondIndex);

    let tier = 'tier1';
    if (POND_UNLOCKS.tier2.ponds.includes(pondIndex)) tier = 'tier2';
    if (POND_UNLOCKS.tier3.ponds.includes(pondIndex)) tier = 'tier3';

    return {
      index: pondIndex,
      name: position?.name || pool?.name || `Pond ${pondIndex + 1}`,
      unlocked,
      tier,
      position: position ? { x: position.x, z: position.z } : null
    };
  }

  /**
   * Get all ponds info
   */
  getAllPondsInfo() {
    return FISHING_HOLE_POSITIONS.map((_, index) => this.getPondInfo(index));
  }

  /**
   * Get rod info
   * @param {number} level - Rod level (1-4)
   */
  getRodInfo(level) {
    return ROD_UPGRADES[level];
  }

  /**
   * Get current rod info
   */
  getCurrentRodInfo() {
    return this.getRodInfo(gameState.state.rodLevel);
  }

  /**
   * Reset progression (for testing or new game)
   */
  reset() {
    gameState.state.unlockedPonds = [0, 1, 2, 3];
    gameState.state.rodLevel = 1;
    this.saveProgress();
  }
}

// Singleton instance
export const progressionSystem = new ProgressionSystem();

export { POND_UNLOCKS, ROD_UPGRADES, POND_FISH_POOLS };
export default ProgressionSystem;
